import { TextInput, MaskedInput, ButtonGroupInput, YesNoInput } from "../components/fields";
import { Card } from "../components/Card";
import { Alert } from "../components/Toast";
import { User, Shield, Monitor } from "lucide-react";
import type { useStepValidation } from "../hooks";
import { useFormStore } from "../store/formStore";

interface StepContactsProps {
  validation: ReturnType<typeof useStepValidation>;
}

const PORTAL_REPLACES_OPTIONS = ["Company Contact", "Fiduciary"];

export function StepContacts({ validation }: StepContactsProps) {
  const fiduciarySameAsContact = useFormStore((s) => s.data.fiduciarySameAsContact);
  const needAlternatePortalAccess = useFormStore((s) => s.data.needAlternatePortalAccess);
  const setField = useFormStore((s) => s.setField);

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-navy mb-1">Contact Information</h1>
      <p className="text-gray-500 mb-6">Who should we reach out to?</p>

      <Card title="Company Contact (Signing Authority)" icon={User}>
        <Alert
          type="info"

          message="This person has authority to sign legal documents on behalf of the company."
          className="mb-4"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <TextInput
            field="contactName"
            label="Full Name"
            validation={validation}
            noPrefill
            placeholder="First and Last Name"
            maxLength={120}
            required
          />
          <TextInput
            field="contactTitle"
            label="Title"
            validation={validation}
            noPrefill
            placeholder="e.g. HR Director"
            maxLength={128}
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput
            field="contactEmail"
            label="Email"
            type="email"
            validation={validation}
            noPrefill
            placeholder="name@company.com"
            maxLength={80}
            required
          />
          <MaskedInput
            field="contactPhone"
            label="Phone"
            validation={validation}
            noPrefill
            mask="phone"
            required
          />
        </div>
      </Card>

      <Card title="Plan Fiduciary" icon={Shield}>
        <Alert
          type="info"

          message="This person manages and controls the employer-sponsored benefit plan."
          className="mb-4"
        />
        <div className="mb-4">
          <YesNoInput
            field="fiduciarySameAsContact"
            label="Same as Company Contact?"
            validation={validation}
            noPrefill
            required
            onChange={(value) => {
              if (value === "Yes") {
                setField("fiduciaryName", "");
                setField("fiduciaryTitle", "");
                setField("fiduciaryEmail", "");
                setField("fiduciaryPhone", "");
                setField("portalReplaces", "");
              }
            }}
          />
        </div>

        {fiduciarySameAsContact === "No" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <TextInput
                field="fiduciaryName"
                label="Full Name"
                validation={validation}
                noPrefill
                placeholder="First and Last Name"
                maxLength={120}
                required
              />
              <TextInput
                field="fiduciaryTitle"
                label="Title"
                validation={validation}
                noPrefill
                placeholder="e.g. CFO"
                maxLength={128}
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                field="fiduciaryEmail"
                label="Email"
                type="email"
                validation={validation}
                noPrefill
                placeholder="name@company.com"
                maxLength={80}
                required
              />
              <MaskedInput
                field="fiduciaryPhone"
                label="Phone"
                validation={validation}
                noPrefill
                mask="phone"
                required
              />
            </div>
          </>
        )}
      </Card>

      <Card title="Allied Portal Access" icon={Monitor}>
        <Alert
          type="info"

          message={
            <>
              <strong>What is Allied Portal Access?</strong> Allied Benefit Systems is Optimyl
              Benefits' Third-Party Administrator (TPA), responsible for the day-to-day
              administration of your health plan, including billing, employee enrollment, and
              keeping your benefits records up to date. The Allied Portal is your online tool to
              manage all of this in one place. By default, the Company Contact and Plan Fiduciary
              listed above will receive login credentials, and portal access is limited to 2
              individuals.
            </>
          }
          className="mb-4"
        />
        <div className="mb-4">
          <YesNoInput
            field="needAlternatePortalAccess"
            label="Need to add an alternate contact?"
            validation={validation}
            required
            noPrefill
            onChange={(value) => {
              if (value === "No") {
                setField("portalContactName", "");
                setField("portalContactEmail", "");
                setField("portalContactPhone", "");
                setField("portalReplaces", "");
              }
            }}
          />
        </div>

        {needAlternatePortalAccess === "Yes" && (
          <>
            <div className="mb-4">
              <TextInput
                field="portalContactName"
                label="Alternate Contact Name"
                validation={validation}
                noPrefill
                placeholder="First and Last Name"
                maxLength={120}
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <TextInput
                field="portalContactEmail"
                label="Email"
                type="email"
                validation={validation}
                noPrefill
                placeholder="name@company.com"
                maxLength={80}
                required
              />
              <MaskedInput
                field="portalContactPhone"
                label="Phone"
                validation={validation}
                noPrefill
                mask="phone"
                required
              />
            </div>
            {fiduciarySameAsContact === "No" && (
              <ButtonGroupInput
                field="portalReplaces"
                label="Who should this person replace?"
                options={PORTAL_REPLACES_OPTIONS}
                validation={validation}
                noPrefill
                required
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
